// External libraries
import { isObject } from '@checkout/react-components';

// Internal modules
import { appStore } from '@/store';
import { features } from '@/lib/features';
import { getLocale } from '@/lib/globals';
import { getAddressMetaKey } from '@/lib/utils/miscUtils';
import { getShippingCountry } from '@/lib/utils/shipping';
import { ADDRESS_VALIDATION_ERROR } from '@/lib/errors/constants';
import { SHIPPING_AND_CALLBACK_ADDRESS_FIELDS } from './constants';

/**
 * @function getAddressesToClearPageLevelErrors
 * @description This function returns address types that are different
 *  from `currentAddressType`
 * @param {string} currentAddressType Address which errors were already cleared
 * @returns {string[]} List of addresses to check validation errors
 */
const getAddressesToClearPageLevelErrors = currentAddressType => {
  const addressTypes = ['billingAddress', 'shippingAddress'];

  // Make sure to validate the Residential Address just if its feature is active
  if (features.isActive('hasPrimaryResidentialAddress')) {
    addressTypes.push('residentialAddress');
  }

  // Return the address types other than `currentAddressType`
  return addressTypes.filter(a => a !== currentAddressType);
};

const clearApiAddressValidationError = ({ addressType, field }) => {
  // Return early if the addressType doesn't have a validation error or if the
  // field is not one of the fields associated with the API validation error.
  const { form: formState } = appStore.getState();
  const { hasValidationError } = formState[addressType].meta;
  const { FIELDS } = ADDRESS_VALIDATION_ERROR;
  if (!hasValidationError || !FIELDS.includes(field)) {
    return;
  }

  const { form: formActions, ui: uiActions } = appStore.getActions();

  // Clear error of address type provided
  formActions.updateAddressValidationError({
    action: 'clearError',
    addressType,
    fields: FIELDS,
  });

  // Check if other address types have validation error
  const clearPageLevelValidationError = getAddressesToClearPageLevelErrors(
    addressType,
  ).reduce((containsValidationError, type) => {
    return (
      containsValidationError && !formState[type]?.meta?.hasValidationError
    );
  }, true);

  // If the other set of address fields doesn't also have an address
  // validation error then clear the associated page level error.
  if (clearPageLevelValidationError) {
    uiActions.setPageLevelError('');
  }
};

/**
 * Helpers for getting props for address fields
 */
const getAdditionalCountryProps = props => {
  if (props.type === 'billing') {
    return { value: getLocale().country };
  }

  return {
    blurHandler: props.blurHandler,
    focusHandler: props.focusHandler,
    generateChangeHandlerMeta,
    id: 'shippingCountry',
    name: 'shippingCountry',
    ...props.country,
  };
};

/**
 * @function getAddressTypeByInputId
 * @description Returns the address type based in the input identifier
 * @param  {string} inputId ID of the input analyze
 * @return {string} Address type
 */
const getAddressTypeByInputId = inputId => {
  if (/^billing/.test(inputId)) {
    return 'billing';
  }

  if (/^residential/.test(inputId)) {
    return 'residential';
  }

  return 'shipping';
};

/**
 * @function generateChangeHandlerMeta
 * @param {object} e | event object
 * @returns {object}
 * @description Used to generate metadata that will be passed to
 *  the change handler when it is fired - (e, newValue, oldValue, meta).
 *  This data helps us match the field to the data location in the store.
 */
const generateChangeHandlerMeta = e => ({
  addressType: getAddressTypeByInputId(e.target.id),
  isAddressField: true,
});

/**
 * @function canPayerShipToBilling
 * @returns {boolean}
 * @description Determines if the shipping to billing checkbox should be shown
 * based on if the `blockNonDomesticShipping` feature is active and it is a
 * non-domestic transaction
 */
const canPayerShipToBilling = () => {
  const { api: apiState } = appStore.getState();
  const { country } = getLocale();
  const { country: merchantCountry } = apiState.cartDetails.merchant;
  const isNonDomesticPurchase = merchantCountry && country !== merchantCountry;
  const isShippingRestricted = features.isActive('blockNonDomesticShipping');

  return !(isNonDomesticPurchase && isShippingRestricted);
};

/**
 * @function validateField
 * @param {string} value
 * @param {object} layoutItem
 * @returns {object} Return object looks like the following:
 *  { errors, isValid, patternMismatch, tooLong, tooShort, valueMissing }
 * @description Validates an individual address field based on the layout
 *  parameters from Griffin.
 */
const validateField = (value, layoutItem) => {
  const { isRequired, maxLength, minLength, regex } = layoutItem;
  const pattern = regex && new RegExp(`^${regex}$`);
  const isPresent = Boolean(value.length);
  const result = {
    patternMismatch: isPresent && Boolean(pattern) && !pattern.test(value),
    tooLong: isPresent && Boolean(maxLength) && value.length > maxLength,
    tooShort: isPresent && Boolean(minLength) && value.length < minLength,
    valueMissing: isRequired && !isPresent,
  };

  result.errors = Object.keys(result).filter(key => result[key]);
  result.isValid = !result.errors.length;

  return result;
};

/**
 * @function validateAddressFields
 * @param {object} options
 *  address(object) - address data sent by the merchant
 *  addressMeta(object) - address metadata from Griffin
 *  shouldUpdateValue(boolean) - if true, include the value in the payload
 * @returns {array}
 * @description Validates address fields and returns actions to dispatch
 *  to the appStore to update the address fields. If `shouldUpdateValue` is
 *  `true` then the value will be updated to what the merchant sent.
 */
const validateAddressFields = ({
  address = {},
  addressMeta = {},
  shouldUpdateValue = false,
}) => {
  const { layout = [], strings = {} } = addressMeta;
  const { stateList } = strings;
  return layout.reduce((accum, layoutItem) => {
    // Address fields that the merchant doesn't send a value for will be
    // `null`. This is often the case with `line2`, which is optional.
    const fieldValue = address[layoutItem.name] || '';

    /**
     * This function is used to validate address data that comes from the
     * merchant and the store (after shipping country change).
     * Merchant format: {
     *   address: {
     *     city: 'My city',
     *   },
     * };
     *
     * Store format: {
     *   address: {
     *     city: { value: 'My city' },
     *   },
     * };
     */
    const isAddressValueFromStore = isObject(fieldValue);
    let value = isAddressValueFromStore ? fieldValue.value : fieldValue;
    let forceUpdateValue;

    // If we don't recognize the `state` returned because it is not
    // in the stateList then we need to update it or ignore it
    if (layoutItem.name === 'state' && value && Array.isArray(stateList)) {
      // Values in the stateList are uppercase
      const upcaseValue = value.toUpperCase();
      const valueFoundInStateList = Boolean(
        stateList.find(state => state.value === upcaseValue),
      );

      // If this is prefill data and the state is not found the the stateList
      // then well just ignore the value sent by the merchant
      if (!valueFoundInStateList && !isAddressValueFromStore) {
        return accum;
      }

      // If this is happening after a user selects a state and then changes
      // country then that state, if not found in the new country's stateList
      // is not valid, so we reset the state to `''`.
      value = valueFoundInStateList ? upcaseValue : '';
      forceUpdateValue = true;
    }

    const { isValid, errors } = validateField(value, layoutItem);

    return {
      ...accum,
      [layoutItem.name]: {
        errorCode: errors[0] || '',
        isValid,
        ...(isValid && { displayError: false }),
        ...((shouldUpdateValue || forceUpdateValue) && { value }),
      },
    };
  }, {});
};

const isStateAndPostalCodeValidForShipping = () => {
  const { form: formState } = appStore.getState();
  const { billingAddress, shipToBilling, shippingAddress } = formState;
  const address = shipToBilling.value ? billingAddress : shippingAddress;
  const { state, postalCode } = address;

  // State and postal code both need to be valid for US and CA
  if (['US', 'CA'].includes(getShippingCountry())) {
    return state.isValid && postalCode.isValid;
  }
  return postalCode.isValid;
};

/**
 * @param {object} address
 * @description Checks whether or not the shipping and callback
 *   address is valid; we'll only check the required fields. Those fields
 *   are determined based on the shipping country. Note that each country
 *   might have different required/optional fields.
 *
 * @returns {boolean}
 */
const isShippingCallbackRequiredAddressValid = (address = {}) => {
  const { api: { griffinMetadata = {} } = {} } = appStore.getState();
  const shippingCountry = getShippingCountry();
  const addressMetaKey = getAddressMetaKey(shippingCountry);

  const addressMetaData = griffinMetadata?.address[addressMetaKey] || {};
  let { layout } = addressMetaData;

  if (!layout) {
    layout = Object.values(griffinMetadata?.address || {})[0]?.layout || [];
  }
  const isAddressValid = layout
    .filter(
      fieldDetails =>
        SHIPPING_AND_CALLBACK_ADDRESS_FIELDS.has(
          fieldDetails?.name?.toLowerCase(),
        ) && fieldDetails.isRequired,
    )
    // `name` should be in camelCase
    .every(({ name }) => address[name]?.isValid);

  return isAddressValid;
};

/**
 * @param {object} addressMetadata
 * @param {string} line1LabelText
 * @description Replaces the line1 label
 * from griffin with value passed in lineLabelText
 *
 * @returns {object} addressMetadata
 */
const updateAddressMetadataForSingleLine = (
  addressMetadata,
  line1LabelText,
) => {
  try {
    const localeData = JSON.parse(JSON.stringify(addressMetadata));
    if (localeData.strings && localeData.strings.line1Label) {
      localeData.strings.line1Label = line1LabelText;
    }
    return localeData;
  } catch (ex) {
    return addressMetadata;
  }
};

/**
 * @param {object} address
 * @description Check the address
 * object for any display errors or
 * for prefilled shipping address or
 * after shipping callback complete
 *
 * @returns {boolean} Override or keep default behavior
 */
const hideAddressSuggestionSingleLine = address => {
  const displayError =
    address?.city?.displayError ||
    address?.postalCode?.displayError ||
    address?.state?.displayError;
  const hasAddressDetails =
    address?.city?.value.length > 0 ||
    address?.postalCode?.value.length > 0 ||
    address?.state?.value.length > 0;
  return !displayError && !hasAddressDetails;
};

export {
  canPayerShipToBilling,
  clearApiAddressValidationError,
  generateChangeHandlerMeta,
  getAdditionalCountryProps,
  getAddressTypeByInputId,
  hideAddressSuggestionSingleLine,
  validateAddressFields,
  isShippingCallbackRequiredAddressValid,
  isStateAndPostalCodeValidForShipping,
  updateAddressMetadataForSingleLine,
};
